(() => {
  const MOBILE_MQ = window.matchMedia('(max-width: 720px)');
  const STAGES_MQ = window.matchMedia('(max-width: 880px)');
  const STAGES_HOVER_MQ = window.matchMedia('(hover: hover) and (pointer: fine)');
  const cleanups = new WeakMap();

  function initReveal(root) {
    const els = root.querySelectorAll('.reveal:not(.in)');
    if (!els.length) return () => {};

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }

  function initStages(root) {
    const track = root.querySelector('[data-stages-track]');
    if (!track) return () => {};

    const stages = [...track.querySelectorAll('[data-stage]')];
    if (!stages.length) return () => {};

    const defaultIndex = Number(track.dataset.defaultStage || 1) - 1;
    let activeId = stages[Math.max(0, defaultIndex)]?.dataset.stageId || stages[0].dataset.stageId;

    const setActive = (stage) => {
      if (!stage) return;
      activeId = stage.dataset.stageId;
      stages.forEach((item) => {
        const isActive = item.dataset.stageId === activeId;
        item.classList.toggle('active', isActive);
        item.classList.toggle('open', isActive && STAGES_MQ.matches);
      });
    };

    const onClick = (event) => {
      if (!STAGES_MQ.matches) return;

      const stage = event.currentTarget;
      const isOpen = stage.classList.contains('open');

      if (!isOpen) {
        event.preventDefault();
        stages.forEach((item) => item.classList.remove('open'));
        setActive(stage);
      }
    };

    const onEnter = (event) => {
      if (STAGES_MQ.matches || !STAGES_HOVER_MQ.matches) return;
      setActive(event.currentTarget);
    };

    const onResize = () => {
      const current = stages.find((stage) => stage.dataset.stageId === activeId) || stages[0];
      setActive(current);
    };

    stages.forEach((stage) => {
      stage.addEventListener('click', onClick);
      stage.addEventListener('mouseenter', onEnter);
    });
    STAGES_MQ.addEventListener('change', onResize);
    onResize();

    return () => {
      stages.forEach((stage) => {
        stage.removeEventListener('click', onClick);
        stage.removeEventListener('mouseenter', onEnter);
      });
      STAGES_MQ.removeEventListener('change', onResize);
    };
  }

  function initScrollBall(root) {
    const wrap = root.querySelector('[data-ball-wrap]');
    if (!wrap || MOBILE_MQ.matches) return () => {};

    const canvas = wrap.querySelector('[data-ball-canvas]');
    const core = wrap.querySelector('[data-ball-core]');
    const orbit = wrap.querySelector('[data-ball-orbit]');
    const fill = wrap.querySelector('[data-ball-fill]');
    const percentEl = wrap.querySelector('[data-ball-percent]');
    const eyebrowEl = wrap.querySelector('[data-ball-eyebrow]');
    const titleEl = wrap.querySelector('[data-ball-title]');
    const keywords = [...wrap.querySelectorAll('[data-ball-keyword]')];
    const rings = {
      r1: wrap.querySelector('[data-ball-ring="r1"]'),
      r2: wrap.querySelector('[data-ball-ring="r2"]'),
      r3: wrap.querySelector('[data-ball-ring="r3"]'),
    };

    const phasesEl = root.querySelector('[data-ball-phases]');
    let phases = [];
    try {
      phases = JSON.parse(phasesEl?.textContent || '[]');
    } catch (error) {
      phases = [];
    }

    const update = () => {
      const rect = wrap.getBoundingClientRect();
      const vh = window.innerHeight;
      const total = Math.max(1, rect.height - vh);
      const scrolled = Math.max(0, -rect.top);
      const progress = Math.min(1, scrolled / total);

      const scale = 0.7 + progress * 0.55;
      const rotate = progress * 360;
      const ringRotate = progress * 180;
      const glow = 0.4 + progress * 0.6;

      if (canvas) {
        canvas.style.transform = `scale(${scale})`;
      }
      if (rings.r1) rings.r1.style.transform = `rotate(${-ringRotate * 0.5}deg)`;
      if (rings.r2) rings.r2.style.transform = `rotate(${ringRotate}deg)`;
      if (rings.r3) rings.r3.style.transform = `rotate(${-ringRotate}deg)`;
      if (orbit) orbit.style.transform = `rotate(${rotate}deg)`;
      if (core) {
        core.style.filter = `brightness(${0.8 + glow * 0.3})`;
        core.style.boxShadow = `0 30px ${60 + progress * 80}px -20px var(--blue-glow), inset 0 -40px 80px rgba(0,0,0,0.5), inset 0 30px 60px rgba(255,255,255,${0.12 + progress * 0.1})`;
      }
      if (fill) fill.style.width = `${progress * 100}%`;
      if (percentEl) percentEl.textContent = `${String(Math.round(progress * 100)).padStart(2, '0')}%`;

      const phaseIdx = Math.min(phases.length - 1, Math.floor(progress * phases.length));
      const phase = phases[phaseIdx];
      if (phase) {
        if (eyebrowEl) eyebrowEl.textContent = phase.eyebrow || '';
        if (titleEl) titleEl.textContent = phase.title || '';
      }

      keywords.forEach((keyword, index) => {
        const baseAngle = Number(keyword.dataset.angle || 0);
        const ang = ((baseAngle + progress * 60 + index * 15) * Math.PI) / 180;
        const radius = 38 + progress * 6;
        const x = 50 + Math.cos(ang) * radius;
        const y = 50 + Math.sin(ang) * radius * 0.55;
        keyword.style.left = `${x}%`;
        keyword.style.top = `${y}%`;
        keyword.style.transform = 'translate(-50%, -50%)';
        keyword.style.opacity = String(0.4 + Math.abs(Math.sin(ang)) * 0.55);
      });
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }

  function initSection(root) {
    if (!root) return;
    destroySection(root);

    const type = root.dataset.bzHomeSection;
    const disposers = [initReveal(root)];

    if (type === 'stages') disposers.push(initStages(root));
    if (type === 'scroll-ball') disposers.push(initScrollBall(root));

    cleanups.set(root, () => disposers.forEach((dispose) => dispose && dispose()));
  }

  function destroySection(root) {
    const dispose = cleanups.get(root);
    if (dispose) dispose();
    cleanups.delete(root);
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[data-bz-home-section]').forEach(initSection);
  }

  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('[data-bz-home-section]') || event.target.closest('[data-bz-home-section]');
    if (root) initSection(root);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-bz-home-section]') || event.target.closest('[data-bz-home-section]');
    if (root) destroySection(root);
  });
})();
